<template>
  <b-container class="charging" :class="{ loading: loading }">
    <b-card>
      <div class="p-0">
        <form ref="formCharging">
          <b-row class="m-0">
            <b-col md="7">
              <h3 class="text-primary">{{ labels.completeData }}</h3>
              <IuguCardForm
                :form="cardInfo"
                :validate="validate"
                ref="iuguCardForm"
              />
              <Address :form="address" :validate="validate" />
              <small class="font-weight-normal"
                >{{ labels.partOne }} <strong>"{{ labels.purchase }}"</strong>
                {{ labels.partTwo }}
                <a :href="labels.link.privacy" target="_blank">{{
                  labels.partThree
                }}</a>
                {{ labels.partFour }}
                <a :href="labels.link.terms" target="_blank">{{
                  labels.partFive
                }}</a
                >.</small
              >
            </b-col>
            <b-col>
              <h3 class="text-primary">{{ labels.resume }}</h3>
              <b-card class="resume-purchase-card">
                <div class="resume-info">
                  <div class="flex-space-between">
                    <span>{{ labels.plan }}</span>
                    <span class="text-primary">{{ labels.enterprise }}</span>
                  </div>

                  <div class="flex-space-between">
                    <span>{{ labels.billing }}</span>
                    <span class="text-primary">{{
                      labels[licensesPlan.period]
                    }}</span>
                  </div>

                  <div class="flex-space-between">
                    <span>{{ labels.licenses }}</span>
                    <span class="text-primary">{{
                      licensesPlan.licenses
                    }}</span>
                  </div>

                  <div class="flex-space-between" v-if="discount">
                    <span>{{ labels.subtotal }}</span>
                    <span class="text-primary"
                      >R$ {{ subtotal | toCurrency }}</span
                    >
                  </div>

                  <div class="flex-space-between" v-if="discount">
                    <span>{{ labels.discount }}</span>
                    <span class="text-primary"
                      >- R$ {{ discount | toCurrency }}</span
                    >
                  </div>
                </div>

                <footer>
                  <b-button
                    variant="red02"
                    @click="purchase"
                    :disabled="loading"
                    >{{ labels.purchase }}</b-button
                  >

                  <div class="text-right">
                    <small>{{ labels.total }}</small>
                    <br />R$ {{ total | toCurrency }}
                  </div>
                </footer>
              </b-card>
            </b-col>
          </b-row>
        </form>
      </div>
      <div class="loading-wrap">
        <Loading />
      </div>
    </b-card>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import IuguCardForm from '@/components/Payment/IuguCardForm.vue'
import Address from '@/components/Payment/Address.vue'
import { createPaymentToken } from '@/service/iugu'
import { subscribe } from '@/service/account'
import Loading from '@/components/Shared/Loading.vue'
import { refreshToken } from '@/service/user'
import { PERIODS } from '@/store/modules/account'
import { HIGH } from '@/helpers/messages-priority'

function getBreadcrumb(labels) {
  const breadcrumb = [
    {
      label: labels.account,
      link: '/account',
    },
    {
      label: labels.payment,
    },
  ]

  return breadcrumb
}

export default {
  components: { IuguCardForm, Address, Loading },
  data() {
    return {
      loading: false,
      validate: false,
      cardInfo: {
        doc: '',
        name: '',
        cardName: '',
        cardNumber: '',
        cardValidate: '',
        cvv: '',
        split: '',
      },
      address: {
        zipcode: '',
        address: '',
        complement: '',
        number: '',
        state: '',
        city: '',
      },
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      licensesPlan: 'account/licensesPlan',
    }),

    labels: function() {
      return Object.assign(
        {},
        this.resources.general.menu,
        this.resources.general.message,
        this.resources.general.termsAndPrivacy,
        this.resources.page.home.accountGeneric,
        this.resources.page.home.charging,
        this.resources.page.login.new
      )
    },

    subtotal: function() {
      const total =
        parseInt(this.licensesPlan.licenses) *
        parseFloat(this.licensesPlan.price)

      return this.licensesPlan.period === PERIODS.ANNUITY ? total * 12 : total
    },

    total: function() {
      const price =
        parseFloat(this.licensesPlan.price) -
        parseFloat(this.licensesPlan.discountPerUser)
      const total = parseInt(this.licensesPlan.licenses) * parseFloat(price)

      return this.licensesPlan.period === PERIODS.ANNUITY ? total * 12 : total
    },

    discount: function() {
      const total =
        parseInt(this.licensesPlan.licenses) *
        parseFloat(this.licensesPlan.discountPerUser)
      return this.licensesPlan.period === PERIODS.ANNUITY ? total * 12 : total
    },
  },

  methods: {
    async purchase() {
      this.loading = true

      try {
        this.validateFields()
        const response = await createPaymentToken(this.cardInfo) // IUGO
        if (response.errors) throw new Error('INVALID_CARD')
        await this.subscribe(response) // API
        await refreshToken()
        this.onPurchaseSuccess()
      } catch (error) {
        this.onPurchaseError(error)
      }

      this.loading = false
    },

    async subscribe(iuguReponse) {
      try {
        const response = await subscribe(
          this.cardInfo.name,
          this.cardInfo.doc,
          this.licensesPlan,
          iuguReponse,
          this.address
        )
        if (response) this.$router.push({ name: 'account.charging.success' })
      } catch (e) {
        throw e
      }
    },

    onPurchaseSuccess() {
      this.$root.$emit('track:checkout', {
        event: 'Order Completed',
        data: {
          plan: 'Enterprise',
          period: this.licensesPlan.period,
          licenses: this.licensesPlan.licenses,
          subtotal: this.subtotal,
          discount: this.discount,
          total: this.total,
        },
      })
    },

    onPurchaseError(error) {
      if (error.message === 'INVALID_CARD') {
        error.message = this.labels.invalidCard
      }

      this.$root.$emit('error', error)

      this.$root.$emit('track:checkout', {
        event: 'Payment Error',
        data: { error: error.message },
      })

      this.$root.$emit('app:message:show', {
        text: error.message,
        status: 'danger',
        priority: HIGH,
      })
    },

    validateFields() {
      this.validate = true
      if (!this.$refs.formCharging.checkValidity()) {
        throw new Error(this.labels.requiredFields)
      }

      if (!this.$refs.iuguCardForm.isValid) {
        throw new Error(this.labels.wrongFields)
      }
    },
  },

  mounted() {
    const breadcrumb = getBreadcrumb(this.labels)
    this.$root.$emit('set-breadcrumb', breadcrumb)

    this.$root.$emit('track:checkout', { event: 'Cart Viewed' })
    this.$root.$emit('setPageTitle', {
      context: this,
      path: 'account/charging',
    })
    this.$root.$emit('track', {
      event: 'Acessou pagina de pagamento',
      data: {
        category: 'Checkout',
      },
    })
  },
}
</script>

<style lang="scss">
.charging {
  .form-group {
    margin-bottom: 2rem !important;
  }

  &.loading {
    position: relative;

    .loading-wrap {
      align-items: center;
      background: #0006;
      border-radius: 8px;
      bottom: 0;
      display: flex;
      justify-content: center;
      left: 0;
      position: absolute;
      right: 0;
      top: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.charging {
  h3 {
    color: map-get($theme-colors, 'primary');
    font-size: 18px;
    font-weight: 500;
    margin: 0;
    margin-bottom: 40px;
    text-transform: uppercase;
  }

  .resume-purchase-card {
    background: map-get($theme-colors, 'light');

    .card-body {
      padding: 0;

      @media (max-width: '425px') {
        padding: 0;
        margin-bottom: 80px;
      }

      .resume-info {
        color: map-get($theme-colors, 'septenary');
        font-weight: 500;
        padding: 1.5rem;

        .flex-space-between {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1rem;

          :first-child {
            text-transform: capitalize;
          }

          &:last-child {
            margin-bottom: 0rem;
          }
        }
      }

      footer {
        background: map-get($theme-colors, 'purple06');
        border-radius: 0 0 8px 8px;
        display: flex;
        justify-content: space-between;
        font-weight: 500;
        text-transform: uppercase;
        padding: 1rem;

        @media (max-width: '425px') {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .btn {
          width: 130px;

          @media (max-width: '425px') {
            width: 150px;
          }
        }

        .text-right {
          color: white;
        }
      }
    }
  }
}
</style>
